"use strict";
'use client';

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CardContext = exports.Card = void 0;
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _index = require("../CardDetails/index.js");
var _index2 = require("../CardImage/index.js");
var _index3 = require("../CardSubtitle/index.js");
var _index4 = require("../CardTitle/index.js");
var _Areas = require("./Areas.js");
var _Box = require("./Box.js");
var _CardButton = require("./CardButton.js");
var _CardLink = require("./CardLink.js");
var _SlotSizes = require("./SlotSizes.js");
var _dataEncoreIds = require("../../dataEncoreIds.js");
var _layoutTheme = require("../../styles/layout-theme.js");
var _utils = require("../../utils.js");
var _excluded = ["pretitle", "title", "subtitle", "media", "leading", "trailing", "body", "footer", "size", "titleGap", "verticalGap", "horizontalGap", "alt", "contentAlign", "disabled", "aria-disabled", "isRedundantOnClick", "isSelected", "onClick", "onClickHint", "id", "draggable", "onDragEnd"];
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { "default": e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n["default"] = e, t && t.set(e, n), n; }
var CardContext = exports.CardContext = /*#__PURE__*/(0, _react.createContext)(null);

/**
 * Cards visually group and vertically stack related content within a container. Cards prioritize large artwork and visual media over scannability. This makes them well-suited for low density surfaces where discovery is key.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-card--docs) |
 * [Encore site](https://encore.spotify.net/web/components/cards) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/Card) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=32403-19604&t=upaMbAqumoI9NOke-1)
 */
var Card = exports.Card = /*#__PURE__*/_react["default"].forwardRef(function Card(_ref, ref) {
  var pretitle = _ref.pretitle,
    title = _ref.title,
    subtitle = _ref.subtitle,
    media = _ref.media,
    leading = _ref.leading,
    trailing = _ref.trailing,
    body = _ref.body,
    footer = _ref.footer,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'md' : _ref$size,
    titleGap = _ref.titleGap,
    _ref$verticalGap = _ref.verticalGap,
    verticalGap = _ref$verticalGap === void 0 ? (0, _layoutTheme.cssSpacing)('tighter-2', '8px') : _ref$verticalGap,
    _ref$horizontalGap = _ref.horizontalGap,
    horizontalGap = _ref$horizontalGap === void 0 ? (0, _layoutTheme.cssSpacing)('tighter', '12px') : _ref$horizontalGap,
    _ref$alt = _ref.alt,
    alt = _ref$alt === void 0 ? '' : _ref$alt,
    _ref$contentAlign = _ref.contentAlign,
    contentAlign = _ref$contentAlign === void 0 ? false : _ref$contentAlign,
    disabled = _ref.disabled,
    ariaDisabled = _ref['aria-disabled'],
    isRedundantOnClick = _ref.isRedundantOnClick,
    isSelected = _ref.isSelected,
    onClick = _ref.onClick,
    onClickHint = _ref.onClickHint,
    id = _ref.id,
    draggable = _ref.draggable,
    onDragEnd = _ref.onDragEnd,
    props = (0, _objectWithoutProperties2["default"])(_ref, _excluded);
  var isDisabled = !!(disabled || ariaDisabled);
  var _useState = (0, _react.useState)(false),
    _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
    primaryFocus = _useState2[0],
    setPrimaryFocus = _useState2[1];
  var cardPadding = _SlotSizes.padding[size];
  var handleClick = function handleClick(e) {
    if (isDisabled) {
      e.preventDefault();
    } else if (onClick !== undefined) {
      onClick(e);
    }
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      if (onClick !== undefined && !isDisabled) {
        onClick(e);
      }
    }
  };
  return /*#__PURE__*/_react["default"].createElement(CardContext.Provider, {
    value: id
  }, /*#__PURE__*/_react["default"].createElement(_Box.Box, Object.assign({
    role: "group",
    "aria-labelledby": "card-title-".concat(id),
    ref: ref,
    isInteractive: !!onClick,
    hasFocus: primaryFocus,
    tabIndex: undefined,
    paddingBlockStart: cardPadding,
    paddingBlockEnd: cardPadding,
    paddingInlineStart: cardPadding,
    paddingInlineEnd: cardPadding,
    $contentAlign: contentAlign,
    disabled: isDisabled,
    $verticalGap: verticalGap,
    "data-encore-id": _dataEncoreIds.ENCORE_DATA_IDS.Card,
    $size: size,
    draggable: draggable,
    onDragEnd: draggable ? function (e) {
      setPrimaryFocus(false);
      onDragEnd === null || onDragEnd === void 0 || onDragEnd(e);
    } : function (e) {
      return onDragEnd === null || onDragEnd === void 0 ? void 0 : onDragEnd(e);
    }
  }, props), onClick && /*#__PURE__*/_react["default"].createElement(_react["default"].Fragment, null, isRedundantOnClick ? /*#__PURE__*/_react["default"].createElement(_CardLink.CardLink, {
    onClick: onClick,
    onMouseDown: function onMouseDown() {
      return setPrimaryFocus(true);
    },
    onTouchStart: function onTouchStart() {
      return setPrimaryFocus(true);
    },
    onMouseUp: function onMouseUp() {
      return setPrimaryFocus(false);
    },
    onTouchEnd: function onTouchEnd() {
      return setPrimaryFocus(false);
    }
  }) : /*#__PURE__*/_react["default"].createElement(_CardButton.CardButton, {
    onClick: function onClick(e) {
      return handleClick(e);
    },
    onKeyDown: function onKeyDown(e) {
      return handleKeyDown(e);
    },
    role: typeof isSelected === 'boolean' ? 'switch' : 'button',
    "aria-disabled": isDisabled,
    "aria-checked": isSelected,
    "aria-labelledby": "card-title-".concat(id, " card-subtitle-").concat(id),
    "aria-describedby": "onClickHint".concat(id),
    onFocus: function onFocus() {
      return setPrimaryFocus(true);
    },
    onBlur: function onBlur() {
      return setPrimaryFocus(false);
    },
    tabIndex: 0
  }), /*#__PURE__*/_react["default"].createElement("div", {
    style: {
      display: 'none'
    },
    id: "onClickHint".concat(id)
  }, onClickHint)), media && (0, _utils.renderConfigOrCustom)(media, /*#__PURE__*/_react["default"].createElement(_index2.CardImage, {
    size: size,
    src: media
  })), /*#__PURE__*/_react["default"].createElement(_Areas.MainArea, {
    $horizontalGap: horizontalGap
  }, leading && /*#__PURE__*/_react["default"].createElement("div", null, leading), /*#__PURE__*/_react["default"].createElement(_Areas.Column, {
    $size: size,
    $titleGap: titleGap
  }, (0, _utils.renderConfigOrCustom)(pretitle, /*#__PURE__*/_react["default"].createElement(_index.CardDetails, null, pretitle)), (0, _utils.renderConfigOrCustom)(title, /*#__PURE__*/_react["default"].createElement(_index4.CardTitle, {
    id: "card-title-".concat(id)
  }, title)), (0, _utils.renderConfigOrCustom)(subtitle, /*#__PURE__*/_react["default"].createElement(_index3.CardSubtitle, null, subtitle))), trailing && /*#__PURE__*/_react["default"].createElement("div", null, trailing)), body && (0, _utils.renderConfigOrCustom)(body, /*#__PURE__*/_react["default"].createElement(_index.CardDetails, null, body)), footer && /*#__PURE__*/_react["default"].createElement(_Areas.FooterArea, null, (0, _utils.renderConfigOrCustom)(footer, /*#__PURE__*/_react["default"].createElement(_index.CardDetails, null, footer)))));
});